import {AllCategoryGroupsDocument} from '@transactions/graphql';
import type {DataProxy} from '@apollo/client';
import type {
  AllCategoryGroupsQuery,
  FullCategoryGroupFragment,
} from '@transactions/graphql';

/**
 * Replace the cached allCategoryGroups list wholesale. Most group mutations
 * (delete/set/reorder) return the full new list, so their update handlers
 * write it straight through.
 */
export const setCategoryGroups = (
  store: DataProxy,
  groups: FullCategoryGroupFragment[],
) => {
  store.writeQuery({
    query: AllCategoryGroupsDocument,
    data: {
      allCategoryGroups: groups,
    },
  });
};

/**
 * Strip a category from every cached group's membership. The server does
 * this on deleteCategory; mirroring it here keeps rearrange mode from
 * submitting the deleted ID (reorderCategoryGroups rejects unknown IDs).
 */
export const removeCategoryFromGroups = (
  store: DataProxy,
  categoryId: string,
) => {
  const data = store.readQuery<AllCategoryGroupsQuery>({
    query: AllCategoryGroupsDocument,
  });
  if (!data) {
    return;
  }
  store.writeQuery({
    query: AllCategoryGroupsDocument,
    data: {
      allCategoryGroups: data.allCategoryGroups.map((group) => ({
        ...group,
        categoryIds: group.categoryIds.filter((id) => id !== categoryId),
      })),
    },
  });
};

/** Append a newly created group to the cached list. */
export const addCategoryGroup = (
  store: DataProxy,
  newGroup: FullCategoryGroupFragment,
) => {
  const data = store.readQuery<AllCategoryGroupsQuery>({
    query: AllCategoryGroupsDocument,
  });
  if (!data) {
    // The query hasn't completed — writing [newGroup] here would make a
    // partial list look complete. Skip; the query's own response will
    // include the new group.
    return;
  }
  const groups = data.allCategoryGroups;
  if (!groups.find((g) => g._id === newGroup._id)) {
    store.writeQuery({
      query: AllCategoryGroupsDocument,
      data: {
        allCategoryGroups: [...groups, newGroup],
      },
    });
  }
};
